/**
 * Die Warteschlange fuer Markierungen (NFR-OFF-01).
 *
 * Die Karte muss ohne Netz funktionieren. Ein Tipp wird darum zuerst
 * **lokal** festgehalten und erst danach verschickt. Was nicht durchgeht,
 * bleibt in IndexedDB liegen, bis es durchgeht.
 *
 * Jede Markierung traegt ihre eigene `markId` und ihre `clientTs`. Die
 * Kennung macht das Nachreichen idempotent, der Zeitstempel sorgt dafuer,
 * dass zehn Minuten Flugmodus nicht in einer Sekunde landen und sich dort
 * gegenseitig bestaetigen.
 */
import { get, set } from 'idb-keyval'

const KEY = 'bsb.queue'
const MAX = 400

export interface QueuedMark {
  mark_id: string
  term_id: string
  action: 'mark' | 'unmark'
  client_ts: string
  room_id: string
  queued: true
}

let memory: QueuedMark[] | null = null

export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

async function read(): Promise<QueuedMark[]> {
  if (memory) return memory
  try {
    memory = ((await get(KEY)) as QueuedMark[] | undefined) ?? []
  } catch {
    memory = []
  }
  return memory
}

async function write(items: QueuedMark[]): Promise<void> {
  memory = items.slice(-MAX)
  try {
    await set(KEY, memory)
  } catch {
    /* Kein IndexedDB: die Warteschlange lebt dann nur im Speicher. */
  }
}

export async function enqueue(item: QueuedMark): Promise<void> {
  const items = await read()
  // Ein zweiter Tipp auf dasselbe Feld hebt einen noch nicht gesendeten
  // ersten auf. Beides zu schicken waere markieren und sofort zuruecknehmen.
  const opposite = items.findIndex(
    (q) => q.room_id === item.room_id && q.term_id === item.term_id && q.action !== item.action,
  )
  if (opposite >= 0) {
    items.splice(opposite, 1)
    await write(items)
    return
  }
  await write([...items.filter((q) => q.mark_id !== item.mark_id), item])
}

export async function pending(roomId?: string): Promise<QueuedMark[]> {
  const items = await read()
  return roomId ? items.filter((q) => q.room_id === roomId) : items
}

export async function drop(ids: string[]): Promise<void> {
  const gone = new Set(ids)
  await write((await read()).filter((q) => !gone.has(q.mark_id)))
}

export async function clear(): Promise<void> {
  await write([])
}

export async function count(roomId?: string): Promise<number> {
  return (await pending(roomId)).length
}
